import { AnyObject } from '@store/types';
import autobind from 'autobind';

import {
    DiagnosisParams,
    DiagnosisType,
    EntryFieldUpdateForm,
    EntryTypeFieldName,
    FieldStatus,
    FieldValueItem,
    ReferralStatus,
    UpdateDiagnosisParams,
} from 'chaika-frontend-types/frontend-cms';
import { RenderedServiceStatus, MedicalCaseStatus } from 'chaika-enums';

import { store } from '@store';
import { IList, IListItem } from '@store/abstract/List';
import { DiagnosisList as DiagnosisListStore } from '@store/models/SystemFields/Diagnosis';
import { EntryApi } from '@api/EntryApi';
import { EhrRelation, ehrService } from '@patient/services/ehr/ehrService';
import { removeNilKeys } from '@utils/removeNiKeys';
import { assertIsDefined } from '@utils/assertIsDefined';

import { referralService } from './referralService';
import { renderedServicesService } from './renderedServicesService';
import { entryLazyUpdater } from '@patient/services/entry/EntryLazyUpdater';
import AwesomeDebouncePromise from 'awesome-debounce-promise';

const SEND_REQUEST_TIMEOUT = 1250;

class UpdateEntryFieldService {
    private hasNotSentMedCaseUpdateRequest = false;
    private fieldNameOnDebounceUpdate?: string;

    // Нужно для обновления филдов. На все филды используется один метод обновления.
    private debounceMap = new Map<string, ReturnType<typeof AwesomeDebouncePromise>>();

    public async updateFieldValue(name: string, value: any, debounce?: boolean, fromTemplate?: boolean): Promise<void> {
        const entry = store.entry;

        if (this.isValueChange(name, value)) {
            entry.setFieldValue(name, value);

            if (fromTemplate) {
                // Без этого, если быстро (с интервалом меньше, чем debounce) применять шаблоны один за другим в середине текста,
                // то они встают не там, где стоит курсор
                setTimeout(async () => this.sendUpdateFieldRequest(name, value, debounce), SEND_REQUEST_TIMEOUT + 100);
            } else {
                if (debounce) {
                    const debounced = this.getDebouncedSender(name);
                    await debounced(value);
                } else {
                    await this.sendUpdateFieldRequest(name, value, false);
                }
            }
        }
    }

    public async updateFieldImmediately(): Promise<void> {
        await Promise.all([entryLazyUpdater.updateImmediately()]);
    }

    public async updateFieldsValue(params: { name: string; value: any }[]): Promise<void> {
        const entry = store.entry;

        await this.updateFields(params);
        params.forEach(({ name, value }) => {
            entry.setFieldValue(name, value);
        });
    }

    public createListField<CP = {}>(name: string, params: CP): void {
        const entry = store.entry;

        const entryList = entry.getFieldValue<IList<CP, any>>(name);

        entryList.add([params]);
    }

    public async updateNewListField<CP = any>(name: string, params: CP): Promise<void> {
        const entry = store.entry;

        const ehrList = entry.getEhrFieldValue<IList<CP, any>>(name);
        const entryList = entry.getFieldValue<IList<CP, any>>(name);

        const value = this.makeUpdateListFieldParams(ehrList.serialize?.() ?? [], [
            ...(entryList.serialize?.() ?? []),
            params,
        ]);

        await this.sendUpdateFieldRequest(name, value, true);
    }

    public async cancelAddNewListFieldItem(name: string) {
        const entry = store.entry;

        const ehrList = entry.getEhrFieldValue<IList<any, any>>(name);
        const entryList = entry.getFieldValue<IList<any, any>>(name);

        const value = this.makeUpdateListFieldParams(serializeList(ehrList), serializeList(entryList));

        await this.sendUpdateFieldRequest(name, value, true);
    }

    public async updateListField<CP = {}>(name: string, id: string, params: Partial<CP>): Promise<void> {
        const entry = store.entry;

        const ehrList = entry.getEhrFieldValue<IList<CP, any>>(name);
        const entryList = entry.getFieldValue<IList<CP, any>>(name);
        const current = entryList.get(id) || ehrList.get(id);
        assertIsDefined(current, `List field ${name} has item ${id}`);
        const updateParams = { ...serializeListItem<CP>(current), status: FieldStatus.Active, ...params };

        ehrList.update(id, updateParams);
        entryList.update(id, updateParams);

        const value = this.makeUpdateListFieldParams(serializeList(ehrList), serializeList(entryList));

        await this.sendUpdateFieldRequest(name, value, true);
    }

    public async updateListFieldId<CP = {}>(name: string, oldId: string, newId: string): Promise<void> {
        const entry = store.entry;

        const ehrList = entry.getEhrFieldValue<IList<CP, any>>(name);
        const entryList = entry.getFieldValue<IList<CP, any>>(name);
        const current = entryList.get(oldId) || ehrList.get(oldId);
        assertIsDefined(current, `List field ${name} has item ${oldId}`);

        const updateParams = { ...serializeListItem<CP>(current), id: newId };

        entryList.update(newId, updateParams);
        entryList.update(oldId, { ...serializeListItem<CP>(current), status: FieldStatus.Deleted });

        const value = this.makeUpdateListFieldParams(serializeList(ehrList), serializeList(entryList));

        await this.sendUpdateFieldRequest(name, value, true);
    }

    public async removeListField(name: string, id: string): Promise<void> {
        const entry = store.entry;

        const ehrList = entry.getEhrFieldValue<IList<any, any>>(name);
        const entryList = entry.getFieldValue<IList<any, any>>(name);
        const current = entryList.get(id) || ehrList.get(id);

        if (current.serialize().parentEntryId === entry.id && !entry.isViewMode) {
            entryList.remove(id);
            ehrList.remove(id);
        } else {
            entryList.update(id, { ...current.serialize(), status: FieldStatus.Deleted });
            ehrList.update(id, { ...current.serialize(), status: FieldStatus.Deleted });
        }

        const value = this.makeUpdateListFieldParams(serializeList(ehrList), serializeList(entryList));

        await this.sendUpdateFieldRequest(name, value, true);
    }

    public async restoreFieldValue(name: string, updateTime: string): Promise<void> {
        const entry = store.entry;

        const field = entry.getVersionField(updateTime, name);
        const value = field ? field.getUpdateParams() : '';

        await entryLazyUpdater.update({
            id: entry.id,
            ehrId: entry.ehrId,
            updateData: {
                [name]: { field: { value } },
            },
        });

        entry.setFieldValue(name, value);
        entry.isViewMode = false;
    }

    public async addMedicalCasesIfNotExist(medicalCaseIds: string[]): Promise<void> {
        const entry = store.entry;
        const entryDiagnosis = entry.getFieldValue<DiagnosisListStore>(EntryTypeFieldName.MedicalCases);
        // Нельзя обернуть в Promise.all тк на беке будет ошибка "транзакция заблокирована"
        for (const id of medicalCaseIds) {
            if (!entryDiagnosis.has(id)) {
                await this.addMedicalCase(id);
            }
        }
    }

    public async addMedicalCase(medicalCaseId: string): Promise<void> {
        const entry = store.entry;

        const entryDiagnosis = entry.getFieldValue<DiagnosisListStore>(EntryTypeFieldName.MedicalCases);
        const ehrDiagnosis = entry.getEhrFieldValue<DiagnosisListStore>(EntryTypeFieldName.MedicalCases);
        const medicalCase = ehrDiagnosis.get(medicalCaseId);

        if (!!medicalCase) {
            await EntryApi.createMedicalCase(entry.id, medicalCase.serialize(), entry.ehrId);

            if (entryDiagnosis) {
                entryDiagnosis.add([medicalCase.serialize()]);
            } else {
                entry.setFieldValue(EntryTypeFieldName.MedicalCases, { diagnosis: [medicalCase.serialize()] });
            }
        }
    }

    public async sendCreateMedicalCaseRequest(params: {
        icdId: string;
        clinicalDiagnosis: string;
        type: DiagnosisType;
        medicalCaseId: string;
        status: MedicalCaseStatus;
    }): Promise<void> {
        const entry = store.entry;

        const entryList = entry.getFieldValue<IList<any, any>>(EntryTypeFieldName.MedicalCases);
        entryList.add([params]);
        await this.sendUpdateFieldRequest(
            EntryTypeFieldName.MedicalCases,
            serializeList(entryList).map(params => ({ id: params.medicalCaseId, ...params, value: params })),
            false,
        );
    }

    public async sendUpdateMedicalCaseRequest(params: UpdateDiagnosisParams): Promise<void> {
        const entry = store.entry;
        const id = params.medicalCaseId;

        this.hasNotSentMedCaseUpdateRequest = true;

        const entryList = entry.getFieldValue<IList<any, any>>(EntryTypeFieldName.MedicalCases);
        const current: IListItem<any> = entryList.get(id);

        if (!!current) {
            const updateParams = { ...serializeListItem(current), ...params };
            entryList.update(id, updateParams);

            await this.sendUpdateFieldRequest(
                EntryTypeFieldName.MedicalCases,
                serializeList(entryList).map(params => ({ id: params.medicalCaseId, ...params, value: params })),
                true,
            );
        }
    }

    public confirmCreateMedicalCase(medicalCase: DiagnosisParams): void {
        const entry = store.entry;

        const entryDiagnosis = entry.getFieldValue<DiagnosisListStore>(EntryTypeFieldName.MedicalCases);

        if (entryDiagnosis) {
            entryDiagnosis.add([medicalCase]);
        } else {
            entry.setFieldValue(EntryTypeFieldName.MedicalCases, { diagnosis: [medicalCase] });
        }
    }

    public async updateMedicalCaseIcd(medicalCaseId: string, icdId: string): Promise<void> {
        const entry = store.entry;

        const diagnosisList = entry.getFieldValue<DiagnosisListStore>(EntryTypeFieldName.MedicalCases);
        const diagnosis = diagnosisList.getExisting(medicalCaseId);

        await this.sendUpdateMedicalCaseRequest({
            icdId,
            medicalCaseId: diagnosis.medicalCaseId,
        });

        await ehrService.loadRelations([EhrRelation.Field]);
        diagnosis.icdId = icdId;
    }

    public async updateMedicalCase({ medicalCaseId, ...params }: UpdateDiagnosisParams): Promise<void> {
        const entry = store.entry;

        const diagnosisList = entry.getFieldValue<DiagnosisListStore>(EntryTypeFieldName.MedicalCases);
        const diagnosis = diagnosisList.getExisting(medicalCaseId);

        Object.keys(removeNilKeys(params)).forEach(key => {
            // @ts-expect-error
            diagnosis[key] = params[key];
        });

        await this.sendUpdateMedicalCaseRequest({
            medicalCaseId,
            ...params,
        });
    }

    public hasMedicalCasePaidRenderedServices(medicalCaseId: string): boolean {
        return renderedServicesService.getByMedicalCaseId(medicalCaseId, RenderedServiceStatus.Paid).length > 0;
    }

    public hasMedicalCaseUnpaidRenderedServices(medicalCaseId: string): boolean {
        const renderedServicesWithSingleMedicalCase = renderedServicesService.getByMedicalCaseId(
            medicalCaseId,
            RenderedServiceStatus.Unpaid,
        );

        return renderedServicesWithSingleMedicalCase.length > 0;
    }

    public hasMedicalCaseActiveReferral(medicalCaseId: string): boolean {
        // const referrals = referralService.getByMedicalCaseId(medicalCaseId, ReferralStatus.Active);
        // const referralsWithSingleMedicalCase = referrals.filter(referral => referral.medicalCaseIds.length === 1);
        return true; // referralsWithSingleMedicalCase.length > 0;
    }

    public hasMedicalCaseExecutedReferral(medicalCaseId: string): boolean {
        return referralService.getByMedicalCaseId(medicalCaseId, ReferralStatus.Executed).length > 0;
    }

    @autobind
    public async deleteMedicalCase(medicalCaseId: string) {
        if (this.hasNotSentMedCaseUpdateRequest) {
            setTimeout(async () => {
                await this.processDeleteMedicalCase(medicalCaseId);
            }, SEND_REQUEST_TIMEOUT + 100);
        } else {
            await this.processDeleteMedicalCase(medicalCaseId);
        }
    }

    @autobind
    public async closeMedicalCase(medicalCaseId: string) {
        if (this.hasNotSentMedCaseUpdateRequest) {
            setTimeout(async () => {
                await this.processCloseMedicalCase(medicalCaseId);
            }, SEND_REQUEST_TIMEOUT + 100);
        } else {
            await this.processCloseMedicalCase(medicalCaseId);
        }
    }

    public async restoreMedicalCase(medicalCaseId: string) {
        const entry = store.entry;

        await Promise.all([
            EntryApi.updateMedicalCase(
                entry.id,
                {
                    medicalCaseId,
                    status: MedicalCaseStatus.Open,
                },
                entry.ehrId,
            ),
        ]);

        const diagnosisList = entry.getFieldValue<DiagnosisListStore>(EntryTypeFieldName.MedicalCases);
        const diagnosis = diagnosisList.getExisting(medicalCaseId);
        diagnosis.status = MedicalCaseStatus.Open;
    }

    private async updateFields(params: { name: string; value: any }[]): Promise<void> {
        const { id, ehrId } = store.entry;
        const fields = params.map(({ name, value }) => ({ name, fieldForm: { field: { value } } }));
        const updateData: AnyObject = {};
        fields.forEach(({ name, fieldForm }) => {
            updateData[name] = fieldForm;
        });
        await entryLazyUpdater.updateImmediately({
            id,
            ehrId,
            updateData,
        });
    }

    private async sendUpdateFieldRequest(name: string, value: any, debounce?: boolean) {
        const { id } = store.entry;

        const isLazyUpdate = debounce && (!this.fieldNameOnDebounceUpdate || this.fieldNameOnDebounceUpdate === name);

        await this.updateFieldLazy(
            {
                id,
                name,
                field: { value },
            },
            !isLazyUpdate,
        );
    }

    @autobind
    private async updateFieldLazy({ id, field, name }: EntryFieldUpdateForm, immediately = false): Promise<void> {
        this.fieldNameOnDebounceUpdate = undefined;
        const { ehrId } = store.entry;

        if (immediately) {
            await entryLazyUpdater.updateImmediately({
                id,
                ehrId,
                updateData: {
                    [name]: { field },
                },
            });
        } else {
            await entryLazyUpdater.update({
                id,
                ehrId,
                updateData: {
                    [name]: { field },
                },
            });
        }
    }

    private async processDeleteMedicalCase(medicalCaseId: string) {
        const entry = store.entry;

        const entryDiagnosis = entry.getFieldValue<DiagnosisListStore>(EntryTypeFieldName.MedicalCases);
        const current = entryDiagnosis.get(medicalCaseId);

        if (!!current) {
            await Promise.all([
                this.sendUpdateFieldRequest(
                    EntryTypeFieldName.MedicalCases,
                    entryDiagnosis
                        .serialize()
                        .filter(params => params.medicalCaseId !== medicalCaseId)
                        .map(params => ({ id: params.medicalCaseId, ...params, value: params })),
                    false,
                ),
                referralService.removeActiveByMedicalCaseId(medicalCaseId),
            ]);
        }

        if (entryDiagnosis.has(medicalCaseId)) {
            entryDiagnosis.remove(medicalCaseId);
        }
    }

    private async processCloseMedicalCase(medicalCaseId: string) {
        const entry = store.entry;

        const entryDiagnosis = entry.getFieldValue<DiagnosisListStore>(EntryTypeFieldName.MedicalCases);
        await Promise.all([
            EntryApi.updateMedicalCase(
                entry.id,
                {
                    medicalCaseId,
                    status: MedicalCaseStatus.Closed,
                },
                entry.ehrId,
            ),
            referralService.removeActiveByMedicalCaseId(medicalCaseId),
        ]);

        const diagnosis = entryDiagnosis.getExisting(medicalCaseId);

        diagnosis.status = MedicalCaseStatus.Closed;
    }

    private getDebouncedSender(name: string): ReturnType<typeof AwesomeDebouncePromise> {
        if (!this.debounceMap.has(name)) {
            const debounced = AwesomeDebouncePromise(
                value => this.sendUpdateFieldRequest(name, value, false),
                SEND_REQUEST_TIMEOUT,
            );
            this.debounceMap.set(name, debounced);
        }

        const sender = this.debounceMap.get(name);
        assertIsDefined(sender, `Debounced sender of ${name} is created above when missing`);

        return sender;
    }

    private makeUpdateListFieldParams(
        ehrValues: { id: string; [key: string]: any }[],
        entryValues: { id: string; [key: string]: any }[],
    ): FieldValueItem[] {
        const entryValueIds = new Set(entryValues.map(({ id }) => id));
        const notChangedValues = ehrValues.filter(({ id }) => !entryValueIds.has(id));

        return [...notChangedValues, ...entryValues].map(param => ({
            id: param.id,
            value: param,
        }));
    }

    private isValueChange(name: string, newValue: any): boolean {
        const entry = store.entry;

        const field = entry.getField(name);

        return !field.isEqual(newValue);
    }
}

export const updateEntryFieldService = new UpdateEntryFieldService();

function serializeList<CreateParams>(list: IList<unknown, CreateParams>): CreateParams[] {
    assertIsDefined(list.serialize, 'Entry list fields implement serialize');
    return list.serialize();
}

function serializeListItem<Params>(item: IListItem<Params>): Params {
    assertIsDefined(item.serialize, 'Entry list items implement serialize');
    return item.serialize();
}
